import { useState, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { apiClient } from "../utils/apiClient";

interface SessionParams {
  user_id: number;
  resp_id: number;
  session_id: number;
}

interface SessionResponse {
  sessionId: number;
  responsibilityId: number;
  lastConnect: string;
  loginId: number;
  userId: number;
  disabledFlag: string;
}

interface ApiResponse {
  success: boolean;
  data?: SessionResponse;
  error?: string;
}

export const useSessionValidation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateSession = useCallback(
    async (
      params: SessionParams,
      callback: (response: SessionResponse | null) => void
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        const { data } = await apiClient.get<ApiResponse>(
          API_CONFIG.ENDPOINTS.GET_SESSION_DTLS,
          {
            params: {
              resp_id: String(params.resp_id),
              session_id: String(params.session_id),
              user_id: String(params.user_id),
            },
          }
        );

        // Handle nested Lambda response
        let responseData = data;
        if (data.data && (data as any).statusCode) {
          if ((data as any).statusCode === 500) {
            const errorBody = JSON.parse((data as any).body);
            throw new Error(errorBody.error || "Session validation error");
          }
          responseData = JSON.parse((data as any).body);
        }

        if (responseData.success && responseData.data && Array.isArray(responseData.data) && responseData.data.length > 0) {
          callback(responseData.data[0]);
        } else {
          throw new Error(responseData.error || "Session validation failed");
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message;
        setError(errorMessage);
        callback(null);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    validateSession,
    isLoading,
    error,
  };
};
